import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { useAuth } from '../auth/googleDomainAuth.jsx';
import useApi from '../hooks/useApi';
import useResultsTableColumnState from '../hooks/results-table/useResultsTableColumnState';
import useResultsTableColumns from '../hooks/results-table/useResultsTableColumns';
import useResultsTableData from '../hooks/results-table/useResultsTableData';
import useResultsTableExport from '../hooks/results-table/useResultsTableExport';
import useResultsTableFilters from '../hooks/results-table/useResultsTableFilters';
import useResultsTableSelection from '../hooks/results-table/useResultsTableSelection';
import useResultsTableTable from '../hooks/results-table/useResultsTableTable';
import { logger } from '../utils/logger';
import { scheduleMrtFilterPopoverOpen } from '../utils/mrtFilterPopover';
import ComparisonView from './ComparisonView.jsx';
import ResultsTableSnackbar from './results-table/ResultsTableSnackbar.jsx';
import ResultsTableSnapshotChips from './results-table/ResultsTableSnapshotChips.jsx';
import ResultsTableToolbar from './results-table/ResultsTableToolbar.jsx';

const ResultsTable = ({
  data,
  isMaximized = false,
  snapshotContext = null,
  onOpenDetail = () => {},
  onRowUpdated = () => {},
}) => {
  const { user } = useAuth();
  const apiHooks = useApi() || {};
  const useDiAccountsByBac = apiHooks.useDiAccountsByBac || (() => ({ mutateAsync: async () => [] }));
  const useDiOrderItemsLiveByBac = apiHooks.useDiOrderItemsLiveByBac || (() => ({ mutateAsync: async () => [] }));
  const { mutateAsync: fetchDiAccountsByBac } = useDiAccountsByBac();
  const { mutateAsync: fetchDiOrderItemsLiveByBac } = useDiOrderItemsLiveByBac();

  const columnStorageKey = useMemo(() => {
    const email = user?.email ? String(user.email).toLowerCase() : null;
    return email ? `resultsTableColumns:${email}` : 'resultsTableColumns:guest';
  }, [user?.email]);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [isCompareViewOpen, setIsCompareViewOpen] = useState(false);

  const {
    globalFilter,
    setGlobalFilter,
    columnFilters,
    setColumnFilters,
    selectedBrandCodes,
    setSelectedBrandCodes,
    resetFilters,
  } = useResultsTableFilters();
  const [rowSelection, setRowSelection] = useState({});
  const [grouping, setGrouping] = useState([]);
  const [editingNotesRowId, setEditingNotesRowId] = useState(null);
  const [editingNotesValue, setEditingNotesValue] = useState('');

  const showToast = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const {
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    columnPinning,
    setColumnPinning,
    initialState: columnState,
  } = useResultsTableColumnState(columnStorageKey);

  const {
    brandFilteredRows,
    matchedTermLegends,
    matchedTermColorMap,
    setRowOverrides,
  } = useResultsTableData({
    data,
    selectedBrandCodes,
    fetchDiAccountsByBac,
    fetchDiOrderItemsLiveByBac,
    showToast,
  });

  const { columns } = useResultsTableColumns({
    editingNotesRowId,
    editingNotesValue,
    setEditingNotesRowId,
    setEditingNotesValue,
    setRowOverrides,
    onRowUpdated,
  });

  const [filterSelectOptionsById, setFilterSelectOptionsById] = useState({});
  const [pendingFilterColumnId, setPendingFilterColumnId] = useState(null);

  const resolvedColumns = useMemo(() => {
    if (!columns) return columns;
    return columns.map((column) => {
      if (column.filterVariant !== 'multi-select') return column;
      const key = column.id || column.accessorKey;
      if (!key) return column;
      const options = filterSelectOptionsById[key];
      if (!Array.isArray(options)) return column;
      return { ...column, filterSelectOptions: options };
    });
  }, [columns, filterSelectOptionsById]);

  useEffect(() => {
    logger.info('ResultsTable (Phase2/MRT) data update', {
      rows: Array.isArray(data) ? data.length : 0,
      isMaximized,
    });
  }, [data, isMaximized]);

  const handleResetFilters = useCallback(
    (tableInstance) => {
      resetFilters();
      try {
        tableInstance?.setColumnFilters?.([]);
        tableInstance?.setGlobalFilter?.('');
        tableInstance?.resetColumnFilters?.();
      } catch (_err) {
        // noop
      }
    },
    [resetFilters]
  );

  const table = useResultsTableTable({
    columns: resolvedColumns,
    data: brandFilteredRows,
    isMaximized,
    onOpenDetail,
    matchedTermColorMap,
    columnState,
    columnVisibility,
    columnOrder,
    columnPinning,
    setColumnVisibility,
    setColumnOrder,
    setColumnPinning,
    rowSelection,
    setRowSelection,
    grouping,
    setGrouping,
    columnFilters,
    setColumnFilters,
    globalFilter,
    setGlobalFilter,
    onRequestAddFilter: setPendingFilterColumnId,
    onResetFilters: handleResetFilters,
  });

  const selectedRows = useResultsTableSelection({ table, rowSelection });

  const { handleCopySelected, handleExportCsv } = useResultsTableExport({
    table,
    selectedRows,
    showToast,
  });

  useEffect(() => {
    if (!pendingFilterColumnId || !table) return;
    return scheduleMrtFilterPopoverOpen({
      table,
      columnId: pendingFilterColumnId,
      buttonSelector: 'button[aria-label*="Filter by"]',
      onComplete: () => setPendingFilterColumnId(null),
    });
  }, [pendingFilterColumnId, table]);

  useEffect(() => {
    if (!table || typeof table.getAllLeafColumns !== 'function') return;
    const next = {};
    table.getAllLeafColumns().forEach((column) => {
      if (!column?.columnDef || column.columnDef.filterVariant !== 'multi-select') return;
      const faceted = column.getFacetedUniqueValues?.();
      if (!faceted) return;
      const options = Array.from(faceted.keys())
        .map((value) => String(value ?? '').trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
      next[column.id] = options;
    });

    setFilterSelectOptionsById((prev) => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (prevKeys.length !== nextKeys.length) return next;
      for (const key of nextKeys) {
        const prevList = prev[key] || [];
        const nextList = next[key] || [];
        if (prevList.length !== nextList.length) return next;
        for (let i = 0; i < nextList.length; i += 1) {
          if (prevList[i] !== nextList[i]) return next;
        }
      }
      return prev;
    });
  }, [brandFilteredRows, columnFilters, globalFilter, table]);

  return (
    <Box sx={{ width: '100%' }}>
      <ResultsTableToolbar
        rowCount={brandFilteredRows.length}
        selectedBrandCodes={selectedBrandCodes}
        onClearBrands={() => setSelectedBrandCodes([])}
        matchedTermLegends={matchedTermLegends}
        selectedRowsCount={selectedRows.length}
        onOpenCompare={() => setIsCompareViewOpen(true)}
        onCopyTsv={() => void handleCopySelected('tsv')}
        onCopyJson={() => void handleCopySelected('json')}
        onExportCsv={() => void handleExportCsv()}
      />

      <ResultsTableSnapshotChips snapshotContext={snapshotContext} />

      <MaterialReactTable table={table} />

      <ComparisonView open={isCompareViewOpen} onClose={() => setIsCompareViewOpen(false)} dealers={selectedRows} />

      <ResultsTableSnackbar
        snackbar={snackbar}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default ResultsTable;
